// src/components/tiptap/StaffMentionExtension.js

function normaliseText(value) {
  return String(value || "").trim().toLowerCase();
}

function dedupeUsers(staffList = []) {
  const seen = new Set();

  return staffList.filter((user) => {
    const idKey = user?.id != null ? `id:${user.id}` : null;
    const emailKey = user?.email ? `email:${normaliseText(user.email)}` : null;
    const fallbackKey = `name:${normaliseText(user?.name)}`;

    const key = idKey || emailKey || fallbackKey;

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function scoreUser(user, query) {
  const q = normaliseText(query);
  const name = normaliseText(user?.name);
  const email = normaliseText(user?.email);

  if (!q) return 0;

  if (name === q || email === q) return 100;
  if (name.startsWith(q) || email.startsWith(q)) return 75;
  if (name.includes(q)) return 50;
  if (email.includes(q)) return 40;

  return 0;
}

export default function StaffMentionExtension(staffList = []) {
  let popupEl = null;

  const createPopup = () => {
    if (!popupEl) {
      popupEl = document.createElement("div");
      popupEl.className =
        "bg-white border rounded shadow text-sm max-h-60 overflow-auto z-50 min-w-[280px]";
      popupEl.style.position = "fixed";
      popupEl.style.left = "0px";
      popupEl.style.top = "0px";
      popupEl.style.display = "none";
      document.body.appendChild(popupEl);
    }
  };

  const destroyPopup = () => {
    if (popupEl && popupEl.parentNode) {
      popupEl.parentNode.removeChild(popupEl);
    }
    popupEl = null;
  };

  const renderItems = (props) => {
    if (!popupEl) return;

    popupEl.innerHTML = "";

    if (!props.items || props.items.length === 0) {
      const empty = document.createElement("div");
      empty.className = "px-3 py-2 text-gray-500";
      empty.textContent = "No users found";
      popupEl.appendChild(empty);
      return;
    }

    props.items.forEach((item, index) => {
      const div = document.createElement("div");
      div.className =
        "px-3 py-2 cursor-pointer hover:bg-blue-100" +
        (index === props.selectedIndex ? " bg-blue-50" : "");

      const nameEl = document.createElement("div");
      nameEl.className = "font-medium";
      nameEl.textContent = item.label;

      div.appendChild(nameEl);

      if (item.email) {
        const emailEl = document.createElement("div");
        emailEl.className = "text-xs text-gray-500";
        emailEl.textContent = item.email;
        div.appendChild(emailEl);
      }

      div.onclick = () => props.command(item);
      popupEl.appendChild(div);
    });
  };

  return {
    items: ({ query }) => {
      const uniqueUsers = dedupeUsers(staffList);

      return uniqueUsers
        .map((user) => ({
          user,
          score: scoreUser(user, query),
        }))
        .filter(({ score }) => (query ? score > 0 : true))
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;

          const aName = normaliseText(a.user?.name);
          const bName = normaliseText(b.user?.name);
          return aName.localeCompare(bName);
        })
        .slice(0, 8)
        .map(({ user }) => ({
          id: user.id,
          label: user.name || user.email || "Unknown user",
          email: user.email || "",
        }));
    },

    render: () => {
      createPopup();

      return {
        onStart: (props) => {
          if (!popupEl) return;
          popupEl.style.display = "block";
          renderItems(props);

          const rect = props.clientRect?.();
          if (rect) {
            const popupHeight = popupEl.offsetHeight || 200;
            const viewportHeight = window.innerHeight;

            // Default: below
            let top = rect.bottom + 4;

            // If not enough space below → flip above
            if (rect.bottom + popupHeight > viewportHeight) {
              top = rect.top - popupHeight - 4;
            }

            popupEl.style.left = `${rect.left}px`;
            popupEl.style.top = `${Math.max(8, top)}px`;
          }
        },

        onUpdate: (props) => {
          if (!popupEl) return;
          renderItems(props);

          const rect = props.clientRect?.();
          if (rect) {
            const popupHeight = popupEl.offsetHeight || 200;
            const viewportHeight = window.innerHeight;

            // Default: below
            let top = rect.bottom + 4;

            // If not enough space below → flip above
            if (rect.bottom + popupHeight > viewportHeight) {
              top = rect.top - popupHeight - 4;
            }

            popupEl.style.left = `${rect.left}px`;
            popupEl.style.top = `${Math.max(8, top)}px`;
          }
        },

        onKeyDown: (props) => {
          if (props.event.key === "ArrowDown") {
            props.command("next");
            return true;
          }

          if (props.event.key === "ArrowUp") {
            props.command("previous");
            return true;
          }

          if (props.event.key === "Enter") {
            props.command("select");
            return true;
          }

          if (props.event.key === "Escape") {
            props.command("hide");
            return true;
          }

          return false;
        },

        onExit: () => {
          if (popupEl) {
            popupEl.style.display = "none";
          }
        },

        destroy: () => {
          destroyPopup();
        },
      };
    },
  };
}